import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Mail, Lock, Key, Eye, EyeOff, Link2, Send, Loader2, CheckCircle } from 'lucide-react';
import { API } from '../lib/api';

const loginTranslations = {
  it: {
    welcome: 'Bentornato',
    subtitle: 'Accedi per gestire il tuo soggiorno',
    emailTab: 'Email',
    codeTab: 'Codice',
    magicTab: 'Link Rapido',
    email: 'Email',
    password: 'Password',
    bookingCode: 'Codice Prenotazione',
    codePlaceholder: 'Es: MDP-ABC123',
    login: 'Accedi',
    loggingIn: 'Accesso...',
    noAccount: 'Non hai un account?',
    register: 'Registrati',
    backHome: 'Torna alla Home',
    welcomeMsg: 'Benvenuto',
    invalidCredentials: 'Credenziali non valide',
    invalidCode: 'Codice non valido',
    forgotPassword: 'Password dimenticata?',
    showPassword: 'Mostra password',
    hidePassword: 'Nascondi password',
    permanentAccount: 'Account Permanente',
    temporaryAccess: 'Accesso Temporaneo',
    magicLinkTitle: 'Accesso senza password',
    magicLinkDesc: 'Ricevi un link di accesso via email',
    sendMagicLink: 'Invia Link di Accesso',
    magicLinkSent: 'Se esiste un account, riceverai il link via email.',
    codeExpired: 'Codice scaduto',
    codeNotActive: 'Codice non ancora attivo'
  },
  en: {
    welcome: 'Welcome Back',
    subtitle: 'Login to manage your stay',
    emailTab: 'Email',
    codeTab: 'Code',
    magicTab: 'Quick Link',
    email: 'Email',
    password: 'Password',
    bookingCode: 'Booking Code',
    codePlaceholder: 'E.g.: MDP-ABC123',
    login: 'Login',
    loggingIn: 'Logging in...',
    noAccount: "Don't have an account?",
    register: 'Register',
    backHome: 'Back to Home',
    welcomeMsg: 'Welcome',
    invalidCredentials: 'Invalid credentials',
    invalidCode: 'Invalid code',
    forgotPassword: 'Forgot password?',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    permanentAccount: 'Permanent Account',
    temporaryAccess: 'Temporary Access',
    magicLinkTitle: 'Passwordless login',
    magicLinkDesc: 'Get a login link via email',
    sendMagicLink: 'Send Access Link',
    magicLinkSent: 'If an account exists, you will receive the link via email.',
    codeExpired: 'Code expired',
    codeNotActive: 'Code not yet active'
  }
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [codice, setCodice] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWithCode, loginGuest } = useAuth();
  const { language } = useLanguage();
  const t = loginTranslations[language] || loginTranslations.it;
  const navigate = useNavigate();

  // Handle permanent account login (guest with verified email + password)
  const handleGuestLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/guest/login`, { email, password });
      if (response.data.success) {
        // Store token and user
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success(`${t.welcomeMsg}, ${response.data.user.nome}!`);
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else {
        toast.error(t.invalidCredentials);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle admin/legacy login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`${t.welcomeMsg}, ${user.nome}!`);
      navigate(user.is_admin ? '/admin' : '/dashboard');
    } catch (error) {
      // If admin login fails, try guest login
      try {
        const response = await axios.post(`${API}/guest/login`, { email, password });
        if (response.data.success) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          toast.success(`${t.welcomeMsg}, ${response.data.user.nome}!`);
          navigate('/dashboard');
          return;
        }
      } catch (guestError) {
        // Show guest login error if more specific
        const detail = guestError.response?.data?.detail;
        if (detail) {
          toast.error(detail);
          return;
        }
      }
      toast.error(error.response?.data?.detail || t.invalidCredentials);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await loginWithCode(codice);
      toast.success(`${t.welcomeMsg}, ${user.nome}!`);
      navigate(user.is_admin ? '/admin' : '/dashboard');
    } catch (error) {
      // B6: Handle booking code expiration errors
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.error) {
        // Structured error from B6
        toast.error(detail.error);
        if (detail.show_alternatives) {
          // Could show alternative login options here
        }
      } else {
        toast.error(detail || t.invalidCode);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // B5: Handle magic link request
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  
  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    setMagicLinkLoading(true);
    try {
      await axios.post(`${API}/guest/request-magic-link`, { email: magicLinkEmail });
      setMagicLinkSent(true);
      toast.success(t.magicLinkSent);
    } catch (error) {
      // Still show success to prevent enumeration
      setMagicLinkSent(true);
    } finally {
      setMagicLinkLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1504644708628-9c1dd99f497f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxQYWVzdHVtJTIwdGVtcGxlJTIwZ3JlZWslMjBydWlucyUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzY2MzU1NTAwfDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-[#1A202C]/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-cinzel text-4xl mb-4">
            {language === 'en' ? 'Welcome back to' : 'Bentornato alla'}<br />Maisonette
          </h2>
          <p className="font-manrope text-gray-300">
            {t.subtitle}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#4A5568] hover:text-[#C5A059] mb-8 font-manrope text-sm transition-colors"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.backHome}
          </Link>

          <div className="mb-8">
            <h1 className="font-cinzel text-3xl text-[#1A202C] mb-2">{t.login}</h1>
            <p className="font-manrope text-[#4A5568]">
              {t.subtitle}
            </p>
          </div>

          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8 bg-[#E2E8F0]">
              <TabsTrigger 
                value="email" 
                className="font-manrope data-[state=active]:bg-[#C5A059] data-[state=active]:text-white text-xs sm:text-sm"
                data-testid="tab-email"
              >
                {t.emailTab}
              </TabsTrigger>
              <TabsTrigger 
                value="code"
                className="font-manrope data-[state=active]:bg-[#C5A059] data-[state=active]:text-white text-xs sm:text-sm"
                data-testid="tab-code"
              >
                {t.codeTab}
              </TabsTrigger>
              <TabsTrigger 
                value="magic"
                className="font-manrope data-[state=active]:bg-[#C5A059] data-[state=active]:text-white text-xs sm:text-sm"
                data-testid="tab-magic"
              >
                {t.magicTab}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="email">
              <form onSubmit={handleEmailLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-manrope text-sm font-medium text-[#4A5568]">
                    {t.email}
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="pl-10 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                      required
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="font-manrope text-sm font-medium text-[#4A5568]">
                    {t.password}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                      required
                      data-testid="input-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#C5A059]"
                      aria-label={showPassword ? t.hidePassword : t.showPassword}
                      data-testid="toggle-password-btn"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="text-right">
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-[#C5A059] hover:underline font-manrope"
                      data-testid="forgot-password-link"
                    >
                      {t.forgotPassword}
                    </Link>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C5A059] hover:bg-[#B08D45] text-white font-cinzel tracking-widest uppercase py-6"
                  data-testid="submit-login-btn"
                >
                  {loading ? 'Accesso in corso...' : 'Accedi'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="code">
              <form onSubmit={handleCodeLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="codice" className="font-manrope text-sm font-medium text-[#4A5568]">
                    Codice Prenotazione
                  </Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
                    <Input
                      id="codice"
                      type="text"
                      value={codice}
                      onChange={(e) => setCodice(e.target.value.toUpperCase())}
                      placeholder="MP-XXXXXXXX"
                      className="pl-10 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059] uppercase"
                      required
                      data-testid="input-codice"
                    />
                  </div>
                  <p className="text-xs text-[#4A5568] font-manrope">
                    Inserisci il codice ricevuto al momento della prenotazione
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C5A059] hover:bg-[#B08D45] text-white font-cinzel tracking-widest uppercase py-6"
                  data-testid="submit-code-btn"
                >
                  {loading ? 'Accesso in corso...' : 'Accedi con Codice'}
                </Button>
              </form>
            </TabsContent>
            
            {/* B5: Magic Link Tab */}
            <TabsContent value="magic">
              {magicLinkSent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-cinzel text-xl text-[#1A202C] mb-2">
                    {language === 'en' ? 'Check your email' : 'Controlla la tua email'}
                  </h3>
                  <p className="text-sm text-[#4A5568] mb-6">
                    {t.magicLinkSent}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setMagicLinkSent(false);
                      setMagicLinkEmail('');
                    }}
                    className="border-[#C5A059] text-[#C5A059]"
                  >
                    {language === 'en' ? 'Try another email' : 'Prova con un\'altra email'}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleMagicLinkRequest} className="space-y-6">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 mx-auto bg-gradient-to-br from-[#1B4332] to-[#2D5A45] rounded-full flex items-center justify-center mb-3">
                      <Link2 className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-cinzel text-lg text-[#1A202C]">{t.magicLinkTitle}</h3>
                    <p className="text-sm text-[#4A5568] font-manrope">{t.magicLinkDesc}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="magic-email" className="font-manrope text-sm font-medium text-[#4A5568]">
                      {t.email}
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
                      <Input
                        id="magic-email"
                        type="email"
                        value={magicLinkEmail}
                        onChange={(e) => setMagicLinkEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="pl-10 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                        required
                        data-testid="input-magic-email"
                      />
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={magicLinkLoading}
                    className="w-full bg-[#1B4332] hover:bg-[#2D5A45] text-white font-cinzel tracking-widest uppercase py-6"
                    data-testid="submit-magic-btn"
                  >
                    {magicLinkLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t.sendMagicLink}
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-[#4A5568] font-manrope">
                    {language === 'en' 
                      ? 'We\'ll send you a link that logs you in instantly. No password needed.'
                      : 'Ti invieremo un link per accedere istantaneamente. Nessuna password richiesta.'}
                  </p>
                </form>
              )}
            </TabsContent>
          </Tabs>

          <p className="mt-8 text-center font-manrope text-sm text-[#4A5568]">
            Non hai un account?{' '}
            <Link 
              to="/register" 
              className="text-[#C5A059] hover:underline font-medium"
              data-testid="register-link"
            >
              Registrati
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
